import {
  GATEWAY_OPERATION_PROTOCOL_FIELDS,
  GatewayOperationError,
  GetGatewayOperationStatusResponse,
  isGatewayOperationLogOutput,
} from "@src/types/gateway-onboarding";
import { SkillType } from "@src/types/skill-types";

/**
 * Shared building blocks of the gateway + machine onboarding journey.
 * Kept in one place so gateway onboarding, machine onboarding and skill
 * installation poll and report operations identically.
 */

export const OPERATION_POLL_INTERVAL_MS = 5000;
export const OPERATION_POLL_MAX_DURATION_MS = 120000;
export const OPERATION_POLL_MAX_ATTEMPTS =
  Math.floor(OPERATION_POLL_MAX_DURATION_MS / OPERATION_POLL_INTERVAL_MS) + 1;

export const waitForNextOperationPoll = (signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    let settled = false;
    function abort(): void {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      reject(
        signal?.reason ||
          new DOMException("Operation polling aborted", "AbortError"),
      );
    }

    const timeout = setTimeout(() => {
      settled = true;
      signal?.removeEventListener("abort", abort);
      resolve();
    }, OPERATION_POLL_INTERVAL_MS);

    if (signal?.aborted) {
      abort();
      return;
    }

    signal?.addEventListener("abort", abort, { once: true });
  });

export interface DetectedMachineSkill {
  type: SkillType;
  port?: number;
}

const GATEWAY_OPERATION_ERROR_KEYS = [
  ...GATEWAY_OPERATION_PROTOCOL_FIELDS.map(([, key]) => key),
  "additional_error_codes",
];

/**
 * Extracts the structured operation error object from an API error payload.
 * Accepts both `{ error: {...} }` envelopes and flat error objects, and returns
 * undefined when the payload carries no usable protocol field.
 */
export const extractGatewayOperationError = (
  payload: unknown,
): GatewayOperationError | undefined => {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  const candidate =
    record.error && typeof record.error === "object"
      ? (record.error as Record<string, unknown>)
      : record;

  const hasProtocolField = GATEWAY_OPERATION_ERROR_KEYS.some((key) => {
    const value = candidate[key];
    if (key === "additional_error_codes") {
      return Array.isArray(value) && value.length > 0;
    }

    if (typeof value === "string") {
      return value.trim().length > 0;
    }

    if (key === "log_output" && isGatewayOperationLogOutput(value)) {
      return Boolean(value.text?.trim()) || value.compressed !== undefined;
    }

    return false;
  });

  return hasProtocolField ? (candidate as GatewayOperationError) : undefined;
};

/**
 * Resolves the error details of a polled operation: the operation's own error
 * object wins, otherwise the raw API error payload is inspected.
 */
export const resolveOperationErrorDetails = (
  operation?: GetGatewayOperationStatusResponse,
  fallback?: unknown,
): GatewayOperationError | undefined =>
  operation?.error || extractGatewayOperationError(fallback);
